import React, { useState, useEffect } from 'react';

import { Box, Alert, Typography, CircularProgress } from '@mui/material';

import { reportBootStage } from 'src/utils/boot/boot-stage';

import { usePreferenceStore } from 'src/store/PreferenceStore';

type PreferenceProviderProps = {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showError?: boolean;
};

/**
 * PreferenceProvider component that loads preferences from the database
 * before rendering children. Similar to StartupPlugins pattern.
 */
export function PreferenceProvider({
  children,
  fallback,
  showError = true,
}: PreferenceProviderProps) {
  const { loadPreferences, isLoaded, isLoading, error } = usePreferenceStore();

  const [initializationComplete, setInitializationComplete] = useState(false);

  useEffect(() => {
    reportBootStage('preferences');
  }, []);

  useEffect(() => {
    const initializePreferences = async () => {
      try {
        console.log('PreferenceProvider: Initializing preferences...');
        await loadPreferences();
        console.log('PreferenceProvider: Preferences loaded successfully');
      } catch (err) {
        console.error('PreferenceProvider: Failed to initialize preferences:', err);
      } finally {
        setInitializationComplete(true);
      }
    };

    initializePreferences();
  }, [loadPreferences]);

  // Show loading state
  if (isLoading || !initializationComplete) {
    return (
      fallback || (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            gap: 2,
          }}
        >
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            Loading preferences...
          </Typography>
        </Box>
      )
    );
  }

  // Show error state (but still render children with defaults)
  if (error && showError) {
    console.warn('PreferenceProvider: Error loading preferences, using defaults:', error);

    return (
      <>
        <Alert
          severity="warning"
          sx={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 9999,
            maxWidth: 400,
          }}
          onClose={() => {
            // Clear error by reloading preferences
            loadPreferences();
          }}
        >
          Failed to load preferences: {error}. Using defaults.
        </Alert>
        {children}
      </>
    );
  }

  // Preferences loaded successfully
  return <>{children}</>;
}

/**
 * Hook to ensure preferences are loaded before component renders
 */
export function useEnsurePreferencesLoaded() {
  const { isLoaded, isLoading, loadPreferences } = usePreferenceStore();

  useEffect(() => {
    if (!isLoaded && !isLoading) {
      loadPreferences();
    }
  }, [isLoaded, isLoading, loadPreferences]);

  return { isLoaded, isLoading };
}

/**
 * Higher-order component to wrap components that need preferences
 */
export function withPreferences<P extends object>(Component: React.ComponentType<P>) {
  return function WrappedComponent(props: P) {
    const { isLoaded } = useEnsurePreferencesLoaded();

    if (!isLoaded) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      );
    }

    return <Component {...props} />;
  };
}
